import { quarterBounds } from '@ergo/shared';
import type { LocalQuarter, QuarterPatientTotals } from '@ergo/shared';
import { type MouseEvent, useState } from 'react';
import { api, errorMessage } from '../api';
import { CELEBRATION_PALETTES, Toast, hearts, petalsOnce, useToast } from '../celebrations';
import { useResource } from '../hooks';
import { formatEuros, formatEurosShort } from '../money';
import { storageKey } from '../storage';
import { useNavCounts } from '../unlogged';
import {
  addMonths,
  currentQuarter,
  deMois,
  firstMonthOfQuarter,
  formatMonthLong,
  formatMonthShort,
  formatShortDate,
  monthOf,
  nextQuarter,
  previousQuarter,
  today,
} from '../time';

/**
 * La déclaration URSSAF du trimestre (docs/redesign-plus-douce.md, §2). Trois étapes,
 * dans l'ordre où elle les vit : passer son relevé bancaire famille par famille,
 * lire le chiffre à recopier, dire que c'est déclaré.
 *
 * Le relevé « C'est bon » reste dans le navigateur : cocher une famille dit « je l'ai
 * regardée sur mon relevé », ce n'est pas une donnée légale et ça n'a aucun sens sur
 * un autre appareil. Le jour de la déclaration, lui, est au serveur — sans quoi
 * l'app redemanderait éternellement (§Données).
 */
export function Declaration({
  quarter,
  onChanged,
}: {
  quarter: LocalQuarter;
  /** Un paiement saisi ici déplace les soldes et les points de la barre. */
  onChanged: () => void;
}) {
  const declaration = useResource(() => api.report.quarter(quarter), [quarter]);
  const counts = useNavCounts();
  const [reviewed, setReviewed] = useReviewed(quarter);
  const [seenFigure, setSeenFigure] = useStep(quarter);
  const { toast, say } = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = declaration.data;
  if (!data) return null;

  const number = quarter.slice(6, 7);
  const label = `T${number}`;
  const lastMonth = addMonths(firstMonthOfQuarter(quarter), 2);
  const opensOn = `${firstMonthOfQuarter(nextQuarter(quarter))}-01`;
  const open = today() >= opensOn;
  const overdue = today() > data.deadline;

  // Le bloc « déclaré » reste affiché jusqu'à la fin du mois de l'échéance, puis
  // l'écran retombe sur la pilule. Déclaré en retard, il reste jusqu'à la fin du
  // mois où elle l'a fait : elle vient de le faire, ça se voit encore.
  const shownUntil =
    data.declaredAt !== null && monthOf(data.declaredAt) > monthOf(data.deadline)
      ? monthOf(data.declaredAt)
      : monthOf(data.deadline);
  const declaredIsFresh = data.declaredAt !== null && monthOf(today()) <= shownUntil;

  // Un trimestre où rien n'a été facturé ni encaissé n'a rien à déclarer : elle
  // n'utilisait pas encore l'app, ou n'a pas travaillé. Seul le trimestre qui vient
  // de finir insiste malgré tout — et seulement si le serveur le réclame aussi
  // (`quarterPending` sait qu'avant le premier encaissement, il n'y a rien à faire).
  const active =
    data.encaisseEur > 0 || data.patients.some((patient) => patient.billedEur > 0);
  const claimed =
    active || (quarter === previousQuarter(currentQuarter()) && counts.quarterPending);

  // La fenêtre n'est pas encore ouverte, mais c'est le trimestre en cours (ou un
  // trimestre qui a déjà de la matière) : on annonce le rendez-vous plutôt que de
  // laisser croire qu'il ne se passera rien ici.
  if (!open && (claimed || quarter === currentQuarter())) {
    return (
      <section className="declaration declaration--avenir">
        <header className="declaration__head">
          <div>
            <h2 className="declaration__title">Déclaration URSSAF · {label}</h2>
            <p className="declaration__sub">s'ouvrira ici le {formatShortDate(opensOn)}</p>
          </div>
        </header>
        <p className="declaration__texte">
          {formatEurosShort(data.encaisseEur)} encaissés pour l'instant · à recopier sur
          urssaf.fr avant le {formatShortDate(data.deadline)}.
        </p>
      </section>
    );
  }

  // Déclaré et retombé : la trace reste, en pilule — c'est le reçu du geste.
  if (data.declaredAt !== null && !declaredIsFresh) {
    return (
      <p className="pilule">
        <span className="pilule__dot" aria-hidden="true" />
        {label} · {formatEurosShort(data.encaisseEur)} encaissés · déclaré le{' '}
        {formatShortDate(data.declaredAt)}
      </p>
    );
  }

  // Rien à déclarer (trimestre d'avant l'app, ou vide) ou fenêtre pas encore là :
  // le silence vaut mieux qu'une pilule d'attente.
  if (!open || !claimed) return null;

  const patients = data.patients;
  // Ce qui part au cabinet se déduit des recettes : le chiffre à recopier est le net.
  // Null tant que le dispositif dort — l'écran est alors exactement celui d'avant.
  const retrocession = data.retrocession;
  const aDeclarerEur = retrocession === null ? data.encaisseEur : retrocession.netEur;
  const left = patients.filter((patient) => !reviewed.has(patient.patientId)).length;
  const step = data.declaredAt !== null ? 3 : seenFigure && left === 0 ? 2 : 1;

  /** Vrai quand le geste est passé : à l'appelant d'en tirer sa fête, s'il en a une. */
  async function run(
    event: MouseEvent,
    message: string | null,
    action: () => Promise<unknown>,
  ): Promise<boolean> {
    setPending(true);
    setError(null);
    try {
      await action();
      hearts(event.clientX, event.clientY, CELEBRATION_PALETTES.paiement);
      if (message !== null) say(message);
      declaration.reload();
      onChanged();
      return true;
    } catch (cause) {
      setError(errorMessage(cause));
      return false;
    } finally {
      setPending(false);
    }
  }

  /** « C'est bon » : la famille est rapprochée, la ligne passe au vert et se tait. */
  function markReviewed(event: MouseEvent, patient: QuarterPatientTotals) {
    hearts(event.clientX, event.clientY, CELEBRATION_PALETTES.paiement);
    const next = new Set(reviewed).add(patient.patientId);
    setReviewed(next);
    if (next.size >= patients.length) say('Relevé passé');
  }

  // Déclarer n'a pas de toast : c'est le bloc fleuri qui le dit, et les pétales.
  async function declare(event: MouseEvent) {
    if (await run(event, null, () => api.report.declareQuarter(quarter))) {
      petalsOnce(storageKey.trimestreDeclare(quarter));
    }
  }

  /** Ce qui est copié est exactement ce qui est affiché : l'euro, pas le centime. */
  async function copyTotal(event: MouseEvent, amountEur: number) {
    const amount = String(Math.round(amountEur));
    try {
      await navigator.clipboard.writeText(amount);
      hearts(event.clientX, event.clientY, CELEBRATION_PALETTES.paiement);
      say('Montant copié');
    } catch {
      setError(`Copie impossible — le montant est ${amount} €.`);
    }
  }

  return (
    <section className="declaration" aria-label={`Déclaration URSSAF ${label}`}>
      {toast && <Toast>{toast}</Toast>}

      <div className="declaration__head">
        <div className="declaration__heading">
          <strong className="declaration__title">Déclaration URSSAF · {label}</strong>
          <span className="declaration__sub">
            {step === 3
              ? 'Terminé'
              : step === 2
                ? 'Étape 2 sur 3 · le chiffre'
                : 'Étape 1 sur 3 · je passe mon relevé'}
          </span>
        </div>
        <div className="declaration__steps" aria-hidden="true">
          {[1, 2, 3].map((index) => (
            <span
              key={index}
              className={`declaration__step${
                index < step || step === 3 ? ' is-done' : index === step ? ' is-current' : ''
              }`}
            />
          ))}
        </div>
      </div>

      {step === 1 && (
        <div className="rapprochement">
          <p className="rapprochement__intro">
            Ouvre ton relevé bancaire {deMois(firstMonthOfQuarter(quarter))} à{' '}
            {formatMonthLong(lastMonth)}. Pour chaque famille, note ce qui est arrivé — ou passe si
            tout est déjà là.
          </p>

          <ul className="rapprochement__list">
            {patients.map((patient) => (
              <LigneReleve
                key={patient.patientId}
                patient={patient}
                reviewed={reviewed.has(patient.patientId)}
                pending={pending}
                onReviewed={(event) => markReviewed(event, patient)}
                onPaid={(event, amountEur) => {
                  // Pas de toast : les cœurs et la sous-ligne qui se recalcule disent
                  // déjà que le paiement est entré.
                  void run(event, null, () =>
                    // Daté du dernier jour du trimestre : on ne connaît pas le jour exact du
                    // virement, et c'est à ce trimestre que la somme doit compter.
                    api.payments.create(patient.patientId, {
                      amountEur,
                      paidOn: quarterBounds(quarter).until,
                    }),
                  );
                }}
              />
            ))}
          </ul>

          <button
            type="button"
            className="declaration__cta"
            disabled={left > 0}
            onClick={() => setSeenFigure(true)}
          >
            {left === 0
              ? 'Voir le chiffre'
              : left === 1
                ? 'Encore une famille'
                : `Encore ${left} familles`}
          </button>
        </div>
      )}

      {step === 2 && (
        <>
          <div className="chiffre">
            <span className="chiffre__kicker">
              {retrocession === null ? 'Recettes du trimestre' : 'Net à déclarer'}
            </span>
            <strong className="chiffre__amount">{formatEurosShort(aDeclarerEur)}</strong>
            {retrocession !== null && (
              <div className="chiffre__retro">
                <p className="chiffre__retro-line">
                  <span>Recettes du trimestre</span>
                  <span>{formatEuros(data.encaisseEur)}</span>
                </p>
                <p className="chiffre__retro-line">
                  <span>Honoraires rétrocédés au cabinet</span>
                  <span>− {formatEuros(retrocession.totalEur)}</span>
                </p>
                <p className="chiffre__retro-line is-net">
                  <span>Net à déclarer</span>
                  <span>{formatEuros(retrocession.netEur)}</span>
                </p>
                <p className="chiffre__retro-note">
                  La rétrocession se déduit des recettes déclarées (à confirmer avec
                  l’expert-comptable).
                </p>
              </div>
            )}
            <span className="chiffre__legend">
              {data.payments.length} paiement{data.payments.length > 1 ? 's' : ''}{' '}
              {deMois(firstMonthOfQuarter(quarter))} à {formatMonthLong(lastMonth)} ·{' '}
              {overdue
                ? `échéance dépassée le ${formatShortDate(data.deadline)}`
                : `à recopier sur urssaf.fr avant le ${formatShortDate(data.deadline)}`}
            </span>
            <div className="chiffre__actions">
              <button
                type="button"
                className="chiffre__copy"
                onClick={(event) => void copyTotal(event, aDeclarerEur)}
              >
                Copier le montant
              </button>
              <button
                type="button"
                className="chiffre__declare"
                disabled={pending}
                onClick={(event) => void declare(event)}
              >
                C’est déclaré
              </button>
            </div>
          </div>
          <button type="button" className="declaration__back" onClick={() => setSeenFigure(false)}>
            Revoir le relevé
          </button>
        </>
      )}

      {step === 3 && data.declaredAt && (
        <div className="declaration__done">
          <span className="declaration__flower" aria-hidden="true">
            ❀
          </span>
          <div className="declaration__done-text">
            <strong>
              {label} déclaré le {formatShortDate(data.declaredAt)}
            </strong>
            <span>
              {formatEurosShort(data.encaisseEur)} · prochain rendez-vous fin{' '}
              {formatMonthLong(firstMonthOfQuarter(nextQuarter(nextQuarter(quarter))))}, pour T
              {nextQuarter(quarter).slice(6, 7)}.
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

interface LigneProps {
  patient: QuarterPatientTotals;
  reviewed: boolean;
  pending: boolean;
  onReviewed: (event: MouseEvent) => void;
  onPaid: (event: MouseEvent, amountEur: number) => void;
}

/**
 * Une famille sur le relevé : ce qui a été facturé dans le trimestre, ce qui est
 * arrivé, et l'écart — « il manque 126 € », jamais « impayé ». Le paiement se saisit
 * ici même, daté du dernier jour du trimestre, pour ne pas quitter le relevé : les
 * montants des factures du trimestre sont proposés en un tap, le champ libre reste.
 */
function LigneReleve({ patient, reviewed, pending, onReviewed, onPaid }: LigneProps) {
  const [amount, setAmount] = useState('');
  const gap = patient.billedEur - patient.receivedEur;
  const settled = gap <= 0;
  const value = Number(amount.replace(',', '.'));
  const valid = amount.trim() !== '' && Number.isFinite(value) && value > 0;

  const tone = reviewed ? ' is-reviewed' : settled ? ' is-settled' : '';

  return (
    <li className={`rapprochement__line${tone}`}>
      <div className="rapprochement__head">
        <span className="rapprochement__label">
          <span className="rapprochement__name">{patient.patientName}</span>
          {/* La barre dit la part reçue d'un coup d'œil ; le mot à droite dit ce qu'il
              reste. Terracotta pour ce qui manque, jamais rouge. */}
          {patient.billedEur > 0 && (
            <span
              className={`rapprochement__barre${settled ? ' is-ok' : ' is-warm'}`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={patient.billedEur}
              aria-valuenow={Math.min(patient.receivedEur, patient.billedEur)}
            >
              <span
                className="rapprochement__barre-fill"
                style={{ width: `${Math.min(100, (patient.receivedEur / patient.billedEur) * 100)}%` }}
              />
            </span>
          )}
          <span className={`rapprochement__sub${settled ? ' is-ok' : ' is-warm'}`}>
            {patient.billedEur > 0 && (
              <span>
                {formatEurosShort(patient.receivedEur)} / {formatEurosShort(patient.billedEur)}
              </span>
            )}
            <span>
              {reviewed
                ? 'rapproché'
                : settled
                  ? gap < 0
                    ? 'tout est là, même un peu d’avance'
                    : patient.billedEur > 0
                      ? 'tout est là'
                      : `${formatEurosShort(patient.receivedEur)} reçus`
                  : `il manque ${formatEurosShort(gap)}`}
            </span>
          </span>
        </span>
        {reviewed ? (
          <span className="rapprochement__check" aria-label="Famille rapprochée">
            ✓
          </span>
        ) : (
          <button type="button" className="rapprochement__seen" onClick={onReviewed}>
            C’est bon
          </button>
        )}
      </div>

      {!reviewed && patient.invoices.length > 0 && (
        <div className="rapprochement__factures">
          {patient.invoices.slice(0, 3).map((invoice) => (
            <button
              key={invoice.noteId}
              type="button"
              className="button button--small"
              disabled={pending}
              title={`Facture ${invoice.number}`}
              onClick={(event) => onPaid(event, invoice.totalEur)}
            >
              {formatMonthShort(invoice.month)} · {formatEurosShort(invoice.totalEur)}
            </button>
          ))}
        </div>
      )}

      {!reviewed && (
        <div className="rapprochement__form">
          <input
            className="rapprochement__amount"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            placeholder="Reçu… €"
            aria-label={`Montant reçu de ${patient.patientName}`}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <button
            type="button"
            className="rapprochement__add"
            disabled={pending || !valid}
            onClick={(event) => {
              onPaid(event, value);
              setAmount('');
            }}
          >
            Ajouter
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Les familles déjà rapprochées, par trimestre. Dans le navigateur : c'est une trace
 * de lecture, pas une donnée du cabinet — et elle se perd sans conséquence.
 */
function useReviewed(quarter: LocalQuarter): [Set<number>, (next: Set<number>) => void] {
  const key = storageKey.releve(quarter);
  const [reviewed, setReviewed] = useState<Set<number>>(() => new Set(readIds(key)));

  function update(next: Set<number>) {
    setReviewed(next);
    write(key, JSON.stringify([...next]));
  }

  return [reviewed, update];
}

/** « Voir le chiffre » franchi : l'étape 2 se retrouve après un rechargement. */
function useStep(quarter: LocalQuarter): [boolean, (next: boolean) => void] {
  const key = storageKey.releveEtape(quarter);
  const [seen, setSeen] = useState(() => read(key) === 'chiffre');

  function update(next: boolean) {
    setSeen(next);
    write(key, next ? 'chiffre' : '');
  }

  return [seen, update];
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readIds(key: string): number[] {
  const raw = read(key);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

function write(key: string, value: string): void {
  try {
    if (value === '') window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Stockage refusé : le relevé se repassera, il ne se perdra pas.
  }
}
